import React, { useState, useMemo, Suspense } from 'react';
import { useTracksStore } from '../../store/tracks.store';
import { useCollectionCache } from '../../services/collectionCache.service';
import { useOrganizationStore } from '../../store/organization.store';

// Lazy-loaded so recharts (~120 kB) only enters the bundle when an admin
// actually opens a report tab — keeps it out of the main chunk.
const CampaignReports = React.lazy(() => import('./CampaignReports'));
// Platform Reports — cross-org, superadmin / master-org only.
const PlatformReports = React.lazy(() => import('./PlatformReports'));

// Resolve a stored field value to a human label. Handles strings, objects
// ({label,name,value}), and ids that need mapping via a reference collection
// (nameById). Falls back to the raw id/value rather than blanking.
const normalizeLabel = (v, nameById) => {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'string') return (nameById && nameById[v]) || v;
  if (typeof v === 'object') {
    return v.label || v.name || v.displayName ||
      (v.value && nameById && nameById[v.value]) || v.value || '';
  }
  return String(v);
};

// Count spots per distinct value of `field`, derived directly from the
// org-scoped audiometa tracks (not a reference collection — so it works for
// languages / statuses that have no collection, and always reflects what's
// actually tagged). Array fields dedupe within a spot. `nameById` maps ids
// to names, `labelMap` normalises display labels, `emptyLabel` buckets
// untagged spots.
function deriveCounts(tracks, { field, isArray, nameById, labelMap, emptyLabel }) {
  const counts = new Map();
  const add = (label) => {
    if (!label) return;
    const mapped = labelMap ? labelMap(label) : label;
    counts.set(mapped, (counts.get(mapped) || 0) + 1);
  };
  for (const track of tracks) {
    const raw = track[field];
    if (isArray) {
      const arr = Array.isArray(raw) ? raw : [];
      const labels = arr.map((x) => normalizeLabel(x, nameById)).filter(Boolean);
      if (labels.length === 0) { if (emptyLabel) add(emptyLabel); continue; }
      for (const l of new Set(labels)) add(l);
    } else {
      const l = normalizeLabel(raw, nameById);
      if (!l) { if (emptyLabel) add(emptyLabel); continue; }
      add(l);
    }
  }
  return Array.from(counts.entries()).map(([name, count]) => ({ id: name, name, count }));
}

// Sort a pre-computed counts array. Defaults to most-common-first.
function useCountSort(counts) {
  const [sortBy, setSortBy] = useState('count');
  const [sortDir, setSortDir] = useState('desc');
  const sortedCounts = [...counts].sort((a, b) => {
    if (sortBy === 'name') {
      if (a.name < b.name) return sortDir === 'asc' ? -1 : 1;
      if (a.name > b.name) return sortDir === 'asc' ? 1 : -1;
      return 0;
    }
    if (a.count < b.count) return sortDir === 'asc' ? -1 : 1;
    if (a.count > b.count) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });
  const handleSort = (column) => {
    if (sortBy === column) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(column); setSortDir(column === 'name' ? 'asc' : 'desc'); }
  };
  return { sortedCounts, sortBy, sortDir, handleSort };
}

const COLORS = [
  '#fd5d93', '#1d8cf8', '#00f2c3', '#ffb236', '#ff8d72', '#5e72e4', '#2dce89', '#f5365c', '#fb6340', '#11cdef',
  '#8965e0', '#f3a4b5', '#e14eca', '#fbc658', '#51cbce', '#6bd098', '#ef8157', '#b8f7d4', '#f7b8b8', '#b8c6f7',
];

const AnalyticsTable = ({ title, counts = [], sortBy, sortDir, handleSort, totalTracks }) => {
  const maxCount = Math.max(...counts.map(c => c.count), 1);
  return (
    <div style={{ marginTop: 32, minWidth: 350, color: 'rgba(255, 255, 255, 0.85)' }}>
      <h4 style={{ color: '#fff' }}>{title}</h4>
      {/* Total Spots row */}
      <div style={{ marginBottom: 12, fontWeight: 700, fontSize: 16, color: '#fff' }}>
        Total Spots: {totalTracks}
      </div>
      <table style={{ minWidth: 500 }}>
        <thead>
          <tr>
            <th
              style={{ textAlign: 'left', paddingRight: 16, cursor: 'pointer', color: 'rgba(255, 255, 255, 0.6)' }}
              onClick={() => handleSort('name')}
            >
              Name {sortBy === 'name' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
            </th>
            <th
              style={{ textAlign: 'right', cursor: 'pointer', color: 'rgba(255, 255, 255, 0.6)' }}
              onClick={() => handleSort('count')}
            >
              Count {sortBy === 'count' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
            </th>
            <th style={{ width: 180, textAlign: 'left', color: 'rgba(255, 255, 255, 0.6)' }}>Bar</th>
            <th style={{ width: 60, textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)' }}>%</th>
          </tr>
        </thead>
        <tbody>
          {counts.map(({ id, name, count }, idx) => (
            <tr key={id}>
              <td style={{ textAlign: 'left', paddingRight: 16, color: 'rgba(255, 255, 255, 0.85)' }}>{name}</td>
              <td style={{ textAlign: 'right', fontWeight: 'bold', paddingRight: 16, color: 'rgba(255, 255, 255, 0.85)' }}>{count}</td>
              <td style={{ width: 180 }}>
                <div style={{
                  height: 18,
                  background: 'transparent',
                  borderRadius: 6,
                  overflow: 'hidden',
                  position: 'relative',
                }}>
                  <div style={{
                    width: `${(count / maxCount) * 100}%`,
                    height: '100%',
                    background: COLORS[idx % COLORS.length],
                    borderRadius: 6,
                    transition: 'width 0.3s',
                  }} />
                </div>
              </td>
              <td style={{ width: 60, textAlign: 'right', fontWeight: 400, color: 'rgba(255, 255, 255, 0.7)' }}>
                {totalTracks > 0 ? ((count / totalTracks) * 100).toFixed(1) : '0.0'}%
              </td>
            </tr>
          ))}
          {/* Total row */}
          <tr>
            <td style={{ textAlign: 'left', fontWeight: 700, paddingRight: 16, color: '#fff' }}>Total</td>
            <td style={{ textAlign: 'right', fontWeight: 700, paddingRight: 16, color: '#fff' }}>
              {counts.reduce((sum, c) => sum + c.count, 0)}
            </td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const cap = (l) => (l ? l.charAt(0).toUpperCase() + l.slice(1).toLowerCase() : l);
const QC_LABELS = { pass: 'Pass', fail: 'Fail', testing: 'Testing' };
const APPROVAL_LABELS = { approved: 'Approved', rejected: 'Rejected', pending: 'Pending' };
const qcLabel = (l) => QC_LABELS[String(l).toLowerCase()] || cap(l);
const approvalLabel = (l) => APPROVAL_LABELS[String(l).toLowerCase()] || cap(l);

const SECTION_OPTIONS = [
  { key: 'clients',   label: 'Clients',         field: 'client',          isArray: false, collection: 'client' },
  { key: 'agencies',  label: 'Agencies',        field: 'agency',          isArray: false, collection: 'agency' },
  { key: 'languages', label: 'Languages',       field: 'language',        isArray: true,  labelMap: cap },
  { key: 'tags',      label: 'Tags',            field: 'tags',            isArray: true,  collection: 'tags' },
  { key: 'qc',        label: 'QC Status',       field: 'qc_status',       isArray: false, labelMap: qcLabel,       emptyLabel: 'Not tested' },
  { key: 'approval',  label: 'Approval Status', field: 'approval_status', isArray: false, labelMap: approvalLabel, emptyLabel: 'Not submitted' },
];

const nameByIdFor = (col) =>
  Object.fromEntries((col || []).map((c) => [c.id, c.displayName || c.name || c.label || c.id]));

const LibraryAnalytics = () => {
  const tracksObj = useTracksStore((state) => state.tracks);
  const clientCol = useCollectionCache((state) => state.client);
  const agencyCol = useCollectionCache((state) => state.agency);
  const tagsCol = useCollectionCache((state) => state.tags);

  const [selectedSection, setSelectedSection] = useState('clients');

  const { counts, totalTracks } = useMemo(() => {
    const tracks = Object.values(tracksObj || {});
    const nameByCollection = {
      client: nameByIdFor(clientCol),
      agency: nameByIdFor(agencyCol),
      tags: nameByIdFor(tagsCol),
    };
    const byKey = {};
    for (const opt of SECTION_OPTIONS) {
      byKey[opt.key] = deriveCounts(tracks, {
        field: opt.field,
        isArray: opt.isArray,
        nameById: opt.collection ? nameByCollection[opt.collection] : undefined,
        labelMap: opt.labelMap,
        emptyLabel: opt.emptyLabel,
      });
    }
    return { counts: byKey, totalTracks: tracks.length };
  }, [tracksObj, clientCol, agencyCol, tagsCol]);

  const selectedOpt = SECTION_OPTIONS.find((o) => o.key === selectedSection) || SECTION_OPTIONS[0];
  const table = useCountSort(counts[selectedSection] || []);
  const sectionContent = (
    <AnalyticsTable
      title={selectedOpt.label}
      counts={table.sortedCounts}
      sortBy={table.sortBy}
      sortDir={table.sortDir}
      handleSort={table.handleSort}
      totalTracks={totalTracks}
    />
  );

  return (
    <div className="pt-3">
      <div style={{ margin: '24px 0 16px 0', display: 'flex', gap: 12 }}>
        {SECTION_OPTIONS.map(opt => (
          <button
            key={opt.key}
            onClick={() => setSelectedSection(opt.key)}
            className={`btn btn-primary-custom btn-round admin-nav-btn admin-analytics-tab-btn${selectedSection === opt.key ? ' selected' : ''}`}
            type="button"
            style={{ opacity: selectedSection === opt.key ? 1 : 0.55 }}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'row', gap: 32, alignItems: 'flex-start' }}>
        {sectionContent}
      </div>
    </div>
  );
};

// Top-level "Reporting & Analytics" page — tabs:
//   Campaign Reports — cross-campaign delivery/QC/review report builder
//   Library Analytics — the original track-count breakdowns
//   Platform Reports — cross-org report (superadmin / master-org admin only)
const Analytics = () => {
  const isSuperAdmin = useOrganizationStore((s) => s.isSuperAdmin);
  const isMasterOrg = useOrganizationStore((s) => s.isMasterOrg);
  const currentOrgRole = useOrganizationStore((s) => s.currentOrgRole);
  // Platform tab is gated the same way as the Global Orgs admin nav item.
  const canSeePlatform = isSuperAdmin || (isMasterOrg && currentOrgRole === 'ADMIN');

  const tabs = [
    { key: 'reports', label: 'Campaign Reports' },
    { key: 'library', label: 'Library Analytics' },
    ...(canSeePlatform ? [{ key: 'platform', label: 'Platform Reports' }] : []),
  ];

  const [tab, setTab] = useState('reports');
  // Guard against a stale 'platform' selection if access changes.
  const activeTab = tabs.some((t) => t.key === tab) ? tab : 'reports';

  return (
    <div className="pt-2">
      <h4 style={{ marginBottom: 8 }}>Reporting &amp; Analytics</h4>
      <div style={{ display: 'flex', gap: 12, borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: 8 }}>
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className="btn btn-link"
            style={{
              padding: '8px 4px',
              margin: 0,
              color: activeTab === t.key ? 'var(--accent-1-color, #6b6bff)' : 'rgba(255,255,255,0.6)',
              borderBottom: activeTab === t.key ? '2px solid var(--accent-1-color, #6b6bff)' : '2px solid transparent',
              borderRadius: 0,
              textDecoration: 'none',
              fontWeight: activeTab === t.key ? 600 : 400,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>Loading…</div>}>
        {activeTab === 'reports' && <CampaignReports />}
        {activeTab === 'library' && <LibraryAnalytics />}
        {activeTab === 'platform' && canSeePlatform && <PlatformReports />}
      </Suspense>
    </div>
  );
};

export default Analytics;